/**
 * task-notify — the single place that turns a task assignment / follow-up into
 * a notification. Writes `mentions/` docs DIRECTLY per recipient (via
 * `createDirectMentions`) → the `onMentionCreated` Cloud Function fans out
 * BOTH the in-app bell and an FCM push. Deep-links to the task on the Plate
 * page via linkPath + sourceNoteId.
 *
 * Deliberately does NOT route through `createMentions`: that path re-resolves
 * synthesised "@firstname" handles through `extractMentions`, which matches
 * the FIRST user with that first name — misrouting on first-name collisions —
 * and re-fires for any @handles the user typed into the task title itself.
 */

import { createDirectMentions } from '@/services/mention.service';
import { MentionEntityType } from '@/types/mention.types';
import type { MentionableUser } from '@/types/mention.types';

export async function notifyTaskAssignees(opts: {
  taskId: string;
  title: string;
  assigneeUserIds: string[];
  actor: { id: string; name: string };
  tenantId: string;
  /** Used only to resolve display names for the notification records. */
  availableUsers: MentionableUser[];
}): Promise<void> {
  const recipients = [...new Set(opts.assigneeUserIds)]
    .filter((id) => id && id !== opts.actor.id)
    .map((id) => {
      const u = opts.availableUsers.find((x) => x.id === id);
      return { userId: id, userName: u?.name || 'Teammate' };
    });

  if (recipients.length === 0) return;

  await createDirectMentions({
    recipients,
    noteContent: `${opts.actor.name} put a task on your plate: ${opts.title}`,
    entityType: MentionEntityType.TODO,
    entityId: opts.taskId,
    entityTitle: `Task: ${opts.title.slice(0, 60)}`,
    createdBy: opts.actor.id,
    createdByName: opts.actor.name,
    tenantId: opts.tenantId,
    linkPath: `/home/plate?taskId=${opts.taskId}`,
    sourceNoteId: opts.taskId,
  });
}
